<script setup>
import { computed, getCurrentInstance, ref, watch } from 'vue'
import { i18n } from '@/i18n'
import { updateReservedStock } from '@/api/reserved-stock'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  item: {
    type: Object,
    default: () => ({}),
  },
  currentReserved: {
    type: Number,
    default: 0,
  },
  warehouseName: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['close', 'success'])

const { $notification } = getCurrentInstance().proxy

const loading = ref(false)
const newReservedStock = ref(0)
const reason = ref('')
const errors = ref({
  reserved: '',
  reason: '',
})

// Max value user can set for reserved stock is now unrestricted (handled by backend)
// Max value for numeric(14,2) is 999,999,999,999.99
// Since we don't allow decimals, the max is 999,999,999,999 (12 digits)
const maxReservedStock = computed(() => 999999999999)

const previewAvailability = computed(() => {
  const availability = Number(props.item?.availability) || 0
  const val = Number(newReservedStock.value)
  const current = Number(props.currentReserved) || 0

  if (!Number.isFinite(val)) return availability

  const deltaVal = val - current
  return availability - deltaVal
})

const movementTypeLabel = computed(() => {
  if (newReservedStock.value > props.currentReserved) {
    return i18n.t('productAvailability.reserved_in')
  }
  if (newReservedStock.value < props.currentReserved) {
    return i18n.t('productAvailability.reserved_out')
  }
  return '-'
})

const delta = computed(() => {
  const val = Number(newReservedStock.value)
  const current = Number(props.currentReserved) || 0
  if (!Number.isFinite(val)) return 0
  return Math.abs(val - current)
})

const hasChanged = computed(() => newReservedStock.value !== props.currentReserved)

const isValueValid = computed(() => {
  const qty = Number(newReservedStock.value)
  if (!Number.isFinite(qty) || qty < 0) return false
  if (String(newReservedStock.value).length > 12) return false
  return true
})

const canSubmit = computed(() => {
  return hasChanged.value && 
         isValueValid.value && 
         !!reason.value?.trim() && 
         reason.value.length <= 200
})

watch(() => props.visible, (val) => {
  if (val) {
    newReservedStock.value = props.currentReserved
    reason.value = ''
    errors.value = { reserved: '', reason: '' }
  }
})

watch(() => newReservedStock.value, (val) => {
  const qty = Number(val)
  if (val && String(val).length > 12) {
    errors.value.reserved = i18n.t('productAvailability.reserved_stock_max_digits') || 'Maksimal 12 digit'
  } else if (Number.isFinite(qty) && qty < 0) {
    errors.value.reserved = i18n.t('productAvailability.reserved_stock_negative')
  } else {
    errors.value.reserved = ''
  }
})

watch(() => reason.value, (val) => {
  if (val && val.length > 200) {
    errors.value.reason = i18n.t('productAvailability.reserved_stock_reason_max_chars')
  } else {
    errors.value.reason = ''
  }
})

function validate() {
  let valid = true
  errors.value = { reserved: '', reason: '' }

  const qty = Number(newReservedStock.value)
  if (!Number.isFinite(qty) || qty < 0) {
    errors.value.reserved = i18n.t('productAvailability.reserved_stock_negative')
    valid = false
  }

  if (!reason.value?.trim()) {
    errors.value.reason = i18n.t('productAvailability.reserved_stock_reason_required')
    valid = false
  }

  return valid
}

async function handleSubmit() {
  if (!validate()) {
    $notification.warning({
      message: i18n.t('productAvailability.reserved_stock_update_failed'),
      description: errors.value.reserved || errors.value.reason,
    })
    return
  }
  if (!hasChanged.value) return

  loading.value = true
  try {
    await updateReservedStock({
      catalog_id: props.item.catalog_id,
      warehouse_id: props.item.warehouse_id,
      business_id: props.item.business_id,
      reserved_stock: newReservedStock.value,
      reason: reason.value.trim(),
    })

    emit('success', {
      catalog_id: props.item.catalog_id,
      warehouse_id: props.item.warehouse_id,
      reserved_stock: newReservedStock.value,
      new_availability: previewAvailability.value,
    })
  } catch (err) {
    const message = err?.response?.data?.message || i18n.t('productAvailability.reserved_stock_update_failed')
    $notification.error({
      message: i18n.t('productAvailability.reserved_stock_update_failed'),
      description: message,
    })
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <a-modal
    :visible="visible"
    :title="$t('productAvailability.edit_reserved_stock')"
    :confirm-loading="loading"
    :ok-text="$t('utils.save') || 'Save'"
    :cancel-text="$t('utils.cancel') || 'Cancel'"
    :ok-button-props="{ props: { disabled: !canSubmit } }"
    width="520px"
    @ok="handleSubmit"
    @cancel="$emit('close')"
  >
    <!-- Product info -->
    <div class="reserved-modal__info mb-3">
      <div class="reserved-modal__info-row">
        <span class="reserved-modal__label">SKU</span>
        <span class="reserved-modal__value reserved-modal__value--mono">{{ item.sku }}</span>
      </div>
      <div class="reserved-modal__info-row">
        <span class="reserved-modal__label">{{ $t('productAvailability.productName') }}</span>
        <span class="reserved-modal__value">{{ item.title }}</span>
      </div>
      <div class="reserved-modal__info-row">
        <span class="reserved-modal__label">{{ $t('productAvailability.warehouse') }}</span>
        <span class="reserved-modal__value">{{ warehouseName }}</span>
      </div>
    </div>

    <a-divider style="margin: 12px 0" />

    <!-- Stock summary -->
    <div class="reserved-modal__stock-summary mb-3">
      <div class="reserved-modal__stock-item">
        <span>{{ $t('productAvailability.reserved_stock_current') }}</span>
        <span class="reserved-modal__stock-value stock-value--reserved">{{ currentReserved }}</span>
      </div>
      <div class="reserved-modal__stock-item">
        <span>{{ $t('productAvailability.available') }}</span>
        <span class="reserved-modal__stock-value stock-value--available">{{ item.availability }}</span>
      </div>
    </div>

    <a-divider style="margin: 12px 0" />

    <!-- Input: New Reserved Stock -->
    <a-form-item
      :label="$t('productAvailability.reserved_stock_new_value')"
      :validate-status="errors.reserved ? 'error' : ''"
      :help="errors.reserved"
      class="mb-4"
    >
      <a-input-number
        v-model="newReservedStock"
        :min="0"
        :precision="0"
        :parser="value => value.replace(/\D/g, '')"
        :formatter="value => value"
        style="width: 100%"
        size="large"
        @focus="$event.target.select()"
      />
    </a-form-item>

    <!-- Input: Reason -->
    <a-form-item
      :label="$t('productAvailability.reserved_stock_reason')"
      :validate-status="errors.reason ? 'error' : ''"
      class="mb-0"
    >
      <a-textarea
        v-model="reason"
        :placeholder="$t('productAvailability.reserved_stock_reason_placeholder')"
        :rows="3"
      />
      <template slot="help">
        <div style="display: flex; justify-content: space-between; width: 100%; margin-top: 4px;">
          <span class="text-danger">{{ errors.reason }}</span>
          <div class="text-muted" style="font-size: 12px; font-weight: normal;">
            <span :class="{ 'text-danger': reason.length > 200 }">{{ reason.length }}</span>/200
          </div>
        </div>
      </template>
    </a-form-item>

    <!-- Preview -->
    <div v-if="hasChanged" class="reserved-modal__preview">
      <div class="reserved-modal__preview-row">
        <span>{{ $t('productAvailability.reserved_stock_movement_type') }}</span>
        <a-tag :color="newReservedStock > currentReserved ? 'orange' : 'green'">
          {{ movementTypeLabel }}
        </a-tag>
      </div>
      <div class="reserved-modal__preview-row">
        <span>{{ $t('productAvailability.reserved_stock_quantity') }}</span>
        <span class="reserved-modal__stock-value">{{ delta }}</span>
      </div>
      <div class="reserved-modal__preview-row">
        <span>{{ $t('productAvailability.reserved_stock_preview') }}</span>
        <span
          class="reserved-modal__stock-value"
          :class="[previewAvailability < 0 ? 'text-danger' : 'stock-value--available']"
        >
          {{ previewAvailability }}
        </span>
      </div>
    </div>
  </a-modal>
</template>

<style lang="scss" scoped>
.reserved-modal {
  &__info {
    background: #f9fafb;
    border-radius: 6px;
    padding: 12px 16px;
  }

  &__info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
  }

  &__label {
    color: #757575;
    font-size: 13px;
  }

  &__value {
    font-weight: 500;
    font-size: 13px;
    max-width: 60%;
    text-align: right;

    &--mono {
      font-family: monospace;
      letter-spacing: 0.5px;
    }
  }

  &__stock-summary {
    display: flex;
    gap: 12px;
  }

  &__stock-item {
    flex: 1;
    background: #f0f2f4;
    border-radius: 6px;
    padding: 10px 12px;
    text-align: center;

    span:first-child {
      display: block;
      font-size: 11px;
      color: #757575;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      margin-bottom: 4px;
    }
  }

  &__stock-value {
    font-weight: 600;
    font-size: 16px;
    color: #1a1a1a;
  }

  &__preview {
    background: #fffbe6;
    border: 1px solid #ffe58f;
    border-radius: 6px;
    padding: 12px 16px;
    margin-top: 20px;
  }

  &__preview-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;

    span:first-child {
      color: #595959;
      font-size: 13px;
    }
  }
}

.stock-value--reserved {
  color: #d46b08 !important;
}

.stock-value--available {
  color: #237804 !important;
}

.text-danger {
  color: #ff4d4f !important;
}

.text-danger {
  color: #ff4d4f;
}
.text-muted {
  color: #8c8c8c;
}
</style>
